import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Ruler } from "lucide-react";
import CoutureLayout from "./CoutureLayout";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

export default function CoutureMeasurements() {
    const { t } = useI18n();
    const [fields, setFields] = useState([]);
    const [key, setKey] = useState("");
    const [label, setLabel] = useState("");
    const [labelEn, setLabelEn] = useState("");
    const [unit, setUnit] = useState("cm");
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/couture/measurement-fields").then((r) => setFields(r.data)).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        if (!key.trim() || !label.trim()) return;
        setBusy(true);
        try {
            await api.post("/couture/measurement-fields", {
                key: key.trim().toLowerCase().replace(/\s+/g, "_"),
                label_fr: label.trim(),
                label_en: labelEn.trim(),
                unit: unit.trim() || "cm",
            });
            setKey("");
            setLabel("");
            setLabelEn("");
            setUnit("cm");
            toast.success(t("couture.field_created"));
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/couture/measurement-fields/${id}`);
            setFields((prev) => prev.filter((f) => f.id !== id));
            toast.success(t("couture.deleted"));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const inputCls =
        "w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-gold transition";
    const labelCls = "text-[0.65rem] tracking-[0.22em] uppercase font-bold text-gray-500";

    return (
        <CoutureLayout>
            <div className="p-6 md:p-10 max-w-3xl" data-testid="couture-measurements-page">
                <h1 className="font-display text-3xl text-brand-navy">{t("couture.measurement_fields")}</h1>
                <div className="w-12 h-px bg-brand-gold mt-2 mb-8" />

                <form onSubmit={create} className="bg-white border border-gray-100 p-6 mb-8">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <label className="block">
                            <span className={labelCls}>{t("couture.field_key")} *</span>
                            <input required value={key} onChange={(e) => setKey(e.target.value)} data-testid="couture-field-key" className={inputCls} placeholder="tour_bras" />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("couture.field_label_fr")} *</span>
                            <input required value={label} onChange={(e) => setLabel(e.target.value)} data-testid="couture-field-label" className={inputCls} placeholder="Tour de bras" />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("couture.field_label_en")}</span>
                            <input value={labelEn} onChange={(e) => setLabelEn(e.target.value)} data-testid="couture-field-label-en" className={inputCls} placeholder="Arm circumference" />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("couture.field_unit")}</span>
                            <input value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="couture-field-unit" className={inputCls} placeholder="cm" />
                        </label>
                    </div>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="couture-field-submit"
                        className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <Plus className="w-4 h-4" /> {t("couture.add_field")}
                    </button>
                </form>

                <div className="space-y-2" data-testid="couture-fields-list">
                    {fields.length === 0 && (
                        <div className="bg-white border border-gray-100 p-8 text-center text-gray-400">{t("couture.no_fields")}</div>
                    )}
                    {fields.map((f) => (
                        <div key={f.id} className="flex items-center justify-between bg-white border border-gray-100 px-5 py-4">
                            <div className="flex items-center gap-3">
                                <Ruler className="w-4 h-4 text-brand-gold" />
                                <div>
                                    <div className="font-semibold text-brand-navy text-sm">{f.label_fr}</div>
                                    <div className="text-xs text-gray-400 font-mono">
                                        {f.key}{f.unit ? ` · ${f.unit}` : ""}{f.label_en ? ` · ${f.label_en}` : ""}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => remove(f.id)}
                                data-testid={`couture-field-delete-${f.key}`}
                                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </CoutureLayout>
    );
}
